import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthState, User } from './types';
import * as api from './services/api';
import { getToken } from './services/auth';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PublicPDFView from './components/PublicPDFView';
import Navbar from './components/Navbar';

const App: React.FC = () => {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setAuth({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }
      try {
        const user = await api.getMe();
        setAuth({ user, isAuthenticated: true, isLoading: false });
      } catch {
        api.logout();
        setAuth({ user: null, isAuthenticated: false, isLoading: false });
      }
    };
    checkAuth();
  }, []);

  const handleLogin = (user: User) => {
    setAuth({ user, isAuthenticated: true, isLoading: false });
  };

  const handleLogout = () => {
    api.logout();
    setAuth({ user: null, isAuthenticated: false, isLoading: false });
  };

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-slate-500 font-medium">Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen flex flex-col">
        {auth.isAuthenticated && <Navbar user={auth.user} onLogout={handleLogout} />}
        <main className="flex-grow">
          <Routes>
            <Route path="/v/:slug" element={<PublicPDFView />} />
            <Route
              path="/dashboard"
              element={auth.isAuthenticated ? <Dashboard user={auth.user!} /> : <Navigate to="/login" />}
            />
            <Route
              path="/login"
              element={!auth.isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />}
            />
            <Route path="/" element={<Navigate to={auth.isAuthenticated ? "/dashboard" : "/login"} />} />
          </Routes>
        </main>

        <footer className="bg-white border-t py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-6 h-6 bg-slate-200 rounded flex items-center justify-center text-slate-500">
                <i className="fas fa-qrcode text-xs"></i>
              </div>
              <span className="font-bold text-slate-400 uppercase tracking-widest text-xs">QR-PDF Linker Platform</span>
            </div>
            <p className="text-slate-400 text-[11px] max-w-xs mx-auto">
              Professional persistent document management. Update content without changing your QR code.
            </p>
            <div className="mt-4 text-slate-300 text-[10px]">
              &copy; {new Date().getFullYear()} Naman Shishangiya &amp; Soham Galande. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </BrowserRouter>
  );
};

export default App;
